import React, { useState, useEffect } from 'react';
import {
    User, LogIn, LogOut, Shield, Key, Trash2, Plus, RefreshCw,
    CheckCircle, AlertCircle, Mail, Edit3, Menu, Settings, Home, ArrowLeft, Book,
    ChevronDown, Zap, Info, LifeBuoy, X
} from 'lucide-react';
import './EnterpriseDMS.css';
import { API_BASE_URL } from '../config';
import ContextHelp from '../components/ContextHelp';


const PROVIDER_COLORS = {
    aws: { bg: '#FF9900', color: '#000', label: 'AWS' },
    gcp: { bg: '#4285F4', color: '#fff', label: 'GCP' },
    azure: { bg: '#0078D4', color: '#fff', label: 'Azure' },
};



/* ─── Credential Form ───────────────────────────────────────────────────── */
function CredentialForm({ onSaved, onCancel }) {
    const [provider, setProvider] = useState('aws');
    const [name, setName] = useState('');
    const [awsKey, setAwsKey] = useState('');
    const [awsSecret, setAwsSecret] = useState('');
    const [gcpProjectId, setGcpProjectId] = useState('');
    const [gcpJson, setGcpJson] = useState('');
    const [azTenant, setAzTenant] = useState('');
    const [azClient, setAzClient] = useState('');
    const [azSecret, setAzSecret] = useState('');
    const [azSub, setAzSub] = useState('');
    const [saving, setSaving] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        let credentials = {};
        if (provider === 'aws') credentials = { accessKeyId: awsKey, secretAccessKey: awsSecret };
        else if (provider === 'gcp') {
            try { 
                credentials = JSON.parse(gcpJson); 
                if (gcpProjectId) credentials.project_id = gcpProjectId;
            }
            catch { alert('Invalid GCP JSON'); return; }
        } else {
            credentials = { tenantId: azTenant, clientId: azClient, clientSecret: azSecret, subscriptionId: azSub };
        }
        setSaving(true);
        try {
            const res = await fetch(`${API_BASE_URL}/api/credentials`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ provider, name, credentials }),
            });
            const data = await res.json();
            if (data.success) onSaved();
            else alert(data.error || 'Failed to save');
        } catch (err) { alert('Error saving credentials'); }
        finally { setSaving(false); }
    };

    const input = { className: 'form-control', style: { fontSize: '0.875rem' } };
    const label = (txt) => (
        <label style={{ display: 'flex', alignItems: 'center', fontSize: '0.75rem', marginBottom: '4px', fontWeight: 500 }}>
            <span style={{ opacity: 0.65 }}>{txt}</span>
            <ContextHelp field={txt} provider={provider} />
        </label>
    );
    const field = (children, mb = '0.75rem') => <div style={{ marginBottom: mb }}>{children}</div>;

    return (
        <form onSubmit={handleSubmit} style={{
            background: 'var(--bg-icon-wrapper)',
            border: '1px solid rgba(168,85,247,0.25)',
            borderRadius: '12px', padding: '1.5rem', marginBottom: '1.5rem'
        }}>
            <h4 style={{ margin: '0 0 1.25rem 0', fontSize: '0.95rem', color: '#a855f7', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Plus size={16} /> Add New Cloud Credential
            </h4>

            {field(<>{label('Cloud Provider')}
                <select value={provider} onChange={e => setProvider(e.target.value)} {...input}>
                    <option value="aws">Amazon Web Services (AWS)</option>
                    <option value="gcp">Google Cloud Platform (GCP)</option>
                    <option value="azure">Microsoft Azure</option>
                </select></>
            )}
            {field(<>{label('Friendly Name')}
                <input required value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Production AWS" {...input} /></>
            )}

            {provider === 'aws' && (<>
                {field(<>{label('Access Key ID')}<input required value={awsKey} onChange={e => setAwsKey(e.target.value)} placeholder="AKIA..." style={{ ...input.style, fontFamily: 'monospace' }} className="form-control" /></>)}
                {field(<>{label('Secret Access Key')}<input required type="password" value={awsSecret} onChange={e => setAwsSecret(e.target.value)} placeholder="••••••••••" {...input} /></>)}
            </>)}

            {provider === 'gcp' && field(<>
                {field(<>{label('Project ID')}<input required value={gcpProjectId} onChange={e => setGcpProjectId(e.target.value)} placeholder="my-gcp-project" style={{ ...input.style, fontFamily: 'monospace' }} className="form-control" /></>)}
                {label('Service Account Key')}
                <textarea required rows={5} value={gcpJson} onChange={e => setGcpJson(e.target.value)}
                    placeholder={'{"type":"service_account","project_id":"..."}'} {...input}
                    style={{ ...input.style, fontFamily: 'monospace', resize: 'vertical', width: '100%', boxSizing: 'border-box' }} />
            </>)}

            {provider === 'azure' && (<>
                {field(<>{label('Tenant ID')}<input required value={azTenant} onChange={e => setAzTenant(e.target.value)} placeholder="xxxxxxxx-xxxx" style={{ ...input.style, fontFamily: 'monospace' }} className="form-control" /></>)}
                {field(<>{label('Client ID')}<input required value={azClient} onChange={e => setAzClient(e.target.value)} placeholder="xxxxxxxx-xxxx" style={{ ...input.style, fontFamily: 'monospace' }} className="form-control" /></>)}
                {field(<>{label('Client Secret')}<input required type="password" value={azSecret} onChange={e => setAzSecret(e.target.value)} placeholder="••••••••••" {...input} /></>)}
                {field(<>{label('Subscription ID')}<input required value={azSub} onChange={e => setAzSub(e.target.value)} placeholder="xxxxxxxx-xxxx" style={{ ...input.style, fontFamily: 'monospace' }} className="form-control" /></>)}
            </>)}

            <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1.25rem' }}>
                <button type="submit" disabled={saving} className="btn-primary" style={{ flex: 1 }}>
                    {saving ? 'Saving…' : 'Save Credential'}
                </button>
                <button type="button" onClick={onCancel} className="btn-secondary" style={{ flex: 1 }}>
                    Cancel
                </button>
            </div>
        </form>
    );
}


/* ─── Main Settings Page (Credentials) ────────────────────────────────────────────────── */
function SettingsPage({ onBack, setActiveView }) {
    const [credentials, setCredentials] = useState([]);
    const [loadingCreds, setLoadingCreds] = useState(true);
    const [isAdding, setIsAdding] = useState(false);

    const fetchCreds = async () => {
        setLoadingCreds(true);
        try {
            const res = await fetch(`${API_BASE_URL}/api/credentials`);
            const data = await res.json();
            if (data.success) setCredentials(data.data);
        } catch (err) {
            console.error("Failed to load credentials:", err);
        } finally { 
            setLoadingCreds(false); 
        }
    };

    useEffect(() => { fetchCreds(); }, []);

    const handleDelete = async (id) => {
        if (!window.confirm('Delete this credential? Bastion auto-discovery for this provider will stop working.')) return;
        await fetch(`${API_BASE_URL}/api/credentials/${id}`, { method: 'DELETE' });
        setCredentials(c => c.filter(x => x.id !== id));
    };

    const [editingCredential, setEditingCredential] = useState(null);
    const [editName, setEditName] = useState('');
    const [savingEdit, setSavingEdit] = useState(false);

    const handleEditClick = (cred) => {
        setIsAdding(false);
        setEditingCredential(cred);
        setEditName(cred.name);
    };

    const handleSaveEdit = async (e) => {
        e.preventDefault();
        setSavingEdit(true);
        try {
            const res = await fetch(`${API_BASE_URL}/api/credentials/${editingCredential.id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ name: editName }),
            });
            
            let data;
            try {
                data = await res.json();
            } catch (jsonErr) {
                // If it's not JSON (like an Express 404 HTML page)
                throw new Error(`Server returned status ${res.status}: ${res.statusText}`);
            }

            if (data.success) {
                setEditingCredential(null);
                fetchCreds();
            } else {
                alert(data.error || 'Failed to update credential');
            }
        } catch (err) {
            alert(err.message || 'Error updating credential');
        } finally {
            setSavingEdit(false);
        }
    };

    const renderLockedField = (labelTxt, value) => (
        <div style={{ marginBottom: '0.75rem' }}>
            <label style={{ display: 'flex', alignItems: 'center', fontSize: '0.75rem', marginBottom: '4px', fontWeight: 500 }}>
                <span style={{ opacity: 0.65 }}>{labelTxt}</span>
            </label>
            <div style={{ position: 'relative' }}>
                <Shield size={14} style={{ position: 'absolute', left: '10px', top: '9px', opacity: 0.5 }} />
                <input 
                    disabled 
                    value={value} 
                    className="form-control" 
                    title="This credential value cannot be modified after creation. Create a new credential if these details change."
                    style={{ paddingLeft: '2rem', opacity: 0.6, cursor: 'not-allowed', fontSize: '0.875rem' }} 
                />
            </div>
        </div>
    );

    const cardStyle = {
        background: 'var(--bg-card, #1a1d2e)',
        border: '1px solid var(--border-color, #2a2a3e)',
        borderRadius: '16px', padding: '1.75rem',
        marginBottom: '1.5rem',
    };
    const sectionTitle = (icon, text, color = '#a855f7') => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '1.5rem' }}>
            <div style={{ background: `${color}22`, borderRadius: '8px', padding: '6px', display: 'flex' }}>
                {React.cloneElement(icon, { size: 18, style: { color } })}
            </div>
            <h2 style={{ margin: 0, fontSize: '1rem', fontWeight: 700 }}>{text}</h2>
        </div>
    );

    return (
        <div className="drawer-panel" onClick={(e) => e.stopPropagation()}>
            <div className="drawer-header">
                <div>
                    <h3 className="drawer-title">Cloud Credentials</h3>
                    <p className="drawer-subtitle">Manage provider access keys for automated provisioning.</p>
                </div>
                <button onClick={onBack} className="drawer-close" title="Close">
                    <X size={20} />
                </button>
            </div>

            <div className="drawer-content">




                {/* ── Cloud Credentials Card ───────────────────── */}
                <div style={cardStyle}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem' }}>
                        {sectionTitle(<Shield />, 'Cloud Credentials', '#a855f7')}
                        {!isAdding && !editingCredential && (
                            <button onClick={() => setIsAdding(true)} className="btn-primary"
                                style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.85rem', marginBottom: '1.5rem' }}>
                                <Plus size={15} /> Add Credential
                            </button>
                        )}
                    </div>

                    {!editingCredential && (
                        <p style={{ margin: '-0.75rem 0 1.25rem 0', fontSize: '0.85rem', opacity: 0.5 }}>
                            Saved credentials are used for automatic Bastion Host discovery during the SSH tunnel setup step.
                        </p>
                    )}

                    {isAdding && (
                        <CredentialForm onSaved={() => { setIsAdding(false); fetchCreds(); }} onCancel={() => setIsAdding(false)} />
                    )}

                    {editingCredential && (
                        <form onSubmit={handleSaveEdit} style={{
                            background: 'var(--bg-icon-wrapper)',
                            border: '1px solid rgba(168,85,247,0.25)',
                            borderRadius: '12px', padding: '1.5rem', marginBottom: '1.5rem'
                        }}>
                            <h4 style={{ margin: '0 0 1.25rem 0', fontSize: '0.95rem', color: '#a855f7', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <Edit3 size={16} /> Edit {PROVIDER_COLORS[editingCredential.provider]?.label || 'Cloud'} Credential
                            </h4>

                            <div style={{ marginBottom: '0.75rem' }}>
                                <label style={{ display: 'flex', alignItems: 'center', fontSize: '0.75rem', marginBottom: '4px', fontWeight: 500 }}>
                                    <span style={{ opacity: 0.65 }}>Credential Name</span>
                                </label>
                                <input required value={editName} onChange={e => setEditName(e.target.value)}
                                    className="form-control" style={{ fontSize: '0.875rem' }} autoFocus />
                            </div>

                            <div style={{ marginTop: '1.5rem', marginBottom: '1.5rem' }}>
                                <div style={{ fontSize: '0.75rem', opacity: 0.5, marginBottom: '1rem', paddingBottom: '0.5rem', borderBottom: '1px solid var(--border-color)' }}>
                                    IMMUTABLE CREDENTIAL VALUES
                                </div>
                                
                                {editingCredential.provider === 'aws' && (
                                    <>
                                        {renderLockedField('Access Key ID', '********')}
                                        {renderLockedField('Secret Access Key', '********')}
                                    </>
                                )}
                                
                                {editingCredential.provider === 'gcp' && (
                                    <>
                                        {renderLockedField('Service Account JSON', '********')}
                                    </>
                                )}
                                
                                {editingCredential.provider === 'azure' && (
                                    <>
                                        {renderLockedField('Tenant ID', '********')}
                                        {renderLockedField('Client ID', '********')}
                                        {renderLockedField('Client Secret', '********')}
                                        {renderLockedField('Subscription ID', '********')}
                                    </>
                                )}
                            </div>

                            <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1.25rem' }}>
                                <button type="submit" disabled={savingEdit} className="btn-primary" style={{ flex: 1 }}>
                                    {savingEdit ? 'Saving…' : 'Save Changes'}
                                </button>
                                <button type="button" onClick={() => setEditingCredential(null)} className="btn-secondary" style={{ flex: 1 }}>
                                    Cancel
                                </button>
                            </div>
                        </form>
                    )}

                    {!isAdding && !editingCredential && (
                        loadingCreds ? (
                            <div style={{ textAlign: 'center', padding: '2rem', opacity: 0.4 }}>
                                <RefreshCw size={24} style={{ animation: 'spin 1.5s linear infinite' }} />
                            </div>
                        ) : credentials.length === 0 ? (
                            <div style={{ textAlign: 'center', padding: '2.5rem', opacity: 0.4 }}>
                                <Key size={40} style={{ marginBottom: '0.75rem' }} />
                                <p style={{ margin: 0, fontSize: '0.9rem' }}>No credentials saved yet.<br />Add one to enable Bastion auto-discovery.</p>
                            </div>
                        ) : (
                            <div>
                                {credentials.map((cred, idx) => {
                                    const p = PROVIDER_COLORS[cred.provider] || { bg: '#666', color: '#fff', label: cred.provider };
                                    return (
                                        <div key={cred.id} style={{
                                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                            padding: '0.9rem 1rem',
                                            background: 'var(--bg-icon-wrapper)',
                                            borderRadius: '10px', marginBottom: '0.6rem',
                                            border: '1px solid var(--border-color)',
                                            transition: 'border-color 0.2s'
                                        }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                                                <span style={{
                                                    background: p.bg, color: p.color,
                                                    padding: '3px 10px', borderRadius: '5px',
                                                    fontSize: '0.7rem', fontWeight: 800, textTransform: 'uppercase',
                                                    letterSpacing: '0.5px', flexShrink: 0
                                                }}>{p.label}</span>
                                                <div>
                                                    <div style={{ fontWeight: 600, fontSize: '0.9rem' }}>{cred.name}</div>
                                                    <div style={{ fontSize: '0.72rem', opacity: 0.45, marginTop: '2px' }}>
                                                        Added {new Date(cred.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}
                                                    </div>
                                                </div>
                                            </div>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                <span style={{
                                                    background: 'rgba(16,185,129,0.12)', color: '#10b981',
                                                    border: '1px solid rgba(16,185,129,0.25)',
                                                    borderRadius: '999px', padding: '2px 10px', fontSize: '0.72rem', fontWeight: 600,
                                                    display: 'flex', alignItems: 'center', gap: '4px'
                                                }}>
                                                    <CheckCircle size={10} /> Active
                                                </span>
                                                <button onClick={() => handleEditClick(cred)}
                                                    style={{ background: 'none', border: 'none', color: 'var(--text-accent, #a855f7)', cursor: 'pointer', padding: '6px', borderRadius: '6px' }}
                                                    title="Edit Credential">
                                                    <Edit3 size={16} />
                                                </button>
                                                <button onClick={() => handleDelete(cred.id)}
                                                    style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', padding: '6px', borderRadius: '6px' }}
                                                    title="Delete Credential">
                                                    <Trash2 size={16} />
                                                </button>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )
                    )}
                </div>



                <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
            </div>

        </div>
    );
}

export default SettingsPage;
